import * as React from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { useRecoilState, useRecoilValue } from 'recoil';
import { botFlowDataAtom, dynamicWebFormPreviewDataAtom, entityPreviewDataAtom, flowObjAtom } from '../../../../Services/_states';
import { Divider, TextField, Typography, TextareaAutosize, Button } from '@mui/material';
import { EntityActions } from '../../../../Services/_actions/Entity/entity.actions.ts';
import { String } from 'aws-sdk/clients/appstream';
import { useParams } from 'react-router';
import { getAllParentNodeResults } from '../../helper.tsx';
import { LeadFormActions } from '../../../../Services/_actions/index.js';

function RenderTextArea({ data }) {
  const botFLowData = useRecoilValue < any > (botFlowDataAtom);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [cursorPosition, setCursorPosition] = React.useState < number | null > (null);

  const [bodyText, setBodyText] = React.useState(data?.body || '');
  const textareaRef = React.useRef < HTMLTextAreaElement > (null);

  const entityActions = EntityActions();
  const leadActions = LeadFormActions();
  const flowObj = useRecoilValue(flowObjAtom);
  const { flowId } = useParams();
  const [contextData, setContextData] = React.useState < any > ({ nodeId: null, nodeLabel: null, fieldType: null });
  const [formField, setFormField] = React.useState < any[] > ([]);
  const [entityPreviewData, setEntityPreviewData] = useRecoilState(entityPreviewDataAtom);
  const [webFormList, setWebFormList] = useRecoilState(dynamicWebFormPreviewDataAtom);

  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);

  // ===== existing bodyText resizing + syncing logic =====
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  React.useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Detect `{{@` pattern
    const match = textBeforeCursor.match(/{{@(\w*)$/);
    if (match) {
      setShowSuggestions(true);
      setContextData({ nodeId: null, nodeLabel: null, fieldType: null });
      setCursorPosition(cursorPos);
    } else {
      setShowSuggestions(false);
    }

    // keep your existing global/webform/result handling
    const matches = value.match(/{{(.*?)}}/g);
    const labelsInBody = matches ? matches.map((m) => m.replace(/{{|}}/g, '')) : [];
    const categorizedParams: {
      global: string[];
      result: string[];
      webform: string[];
      entity: string[];
      lead_data: string[];
      global_lead_data: string[];
    } = {
      global: [],
      result: [],
      webform: [],
      entity: [],
      lead_data: [],
      global_lead_data: []
    };
    labelsInBody.forEach((label) => {
      // if (label.includes(":")) {
      //   categorizedParams.entity = [...categorizedParams.entity, label];
      // } 
      if (label?.includes(":")) {
        const [nodeLabel] = label.split(":");
        const paramObj = parentResults?.find((r: { label: string }) => r.label === nodeLabel);
        const nodes = flowObj?.nodes ?? [];
        const parentNodeData = nodes.find((node: { id: string }) => node?.id === paramObj.node_id);
        if (parentNodeData?.data?.lead_data_setting && parentNodeData?.data?.lead_data_setting?.campaign?.proposal_id) {
          categorizedParams.lead_data = [...categorizedParams.lead_data, label];
        } else if (parentNodeData?.data?.entity_setting?.entity?._id) {
          categorizedParams.entity = [...categorizedParams.entity, label];
        } else {
          categorizedParams.global_lead_data = [...categorizedParams.global_lead_data, label];
        }
      } else {
        const found = parentResults?.find((r: any) => r.label === label);
        if (found) {
          if (found.paramType === "global") {
            categorizedParams.global = [...categorizedParams.global, found.value];
          } else if (found.paramType === "result") {
            categorizedParams.result = [...categorizedParams.result, found.value];
          } else if (found.paramType === "webform") {
            categorizedParams.webform = [...categorizedParams.webform, found.value];
          }
        }
      }
    });

    data.onChange(data.id, { body: value, params: categorizedParams });
  };

  const modifyWebFormListObj = (obj: any) => {
    try {
      if (Object.keys(obj).length === 0) {
        return [];
      }
      return Object.values(obj);
    } catch (error) {
      console.warn(error)
      return []
    }
  }

  // ===== entity specific handling =====
  const hanleSelectParentResult = async (suggestion: any) => {
    const nodes = flowObj?.nodes ?? [];
    const nodeData = nodes.find((node) => node?.id === suggestion.node_id);
    const entity = nodeData?.data?.entity_setting?.entity;
    const lead_data = nodeData?.data?.lead_data_setting;
    if (suggestion.paramType === "global_lead_data") {
      const proposalId = botFLowData?.campaign_id;
      const webFormKey = botFLowData?.lead_form_id;
      if (proposalId && webFormKey) {
        let formFieldData = webFormList?.[proposalId]?.[webFormKey];
        if (!formFieldData) {
          const response = await leadActions.getFormsByCampaignId(proposalId);
          if (response?.status === 200) {
            const leadForms = response.data ?? {};
            formFieldData = leadForms?.[webFormKey];
            setWebFormList((prev) => ({
              ...prev,
              [proposalId]: leadForms,
            }));
          }
        }
        if (formFieldData) {
          const updateData = modifyWebFormListObj(formFieldData?.leads_form_items || [])?.map(
            (item: any) => ({
              key_name: item?.key_name,
              // alias_name: item?.alias_name,
              label: item?.alias_name,
              name: item?.key_name,
            })
          );
          setFormField(updateData);
          setContextData({ nodeId: "global_lead_data", nodeLabel: suggestion.label, fieldType: "global_lead_data" });
        }
      }
    } else if (suggestion?.node_id && entity) {
      try {
        if (!entity?._id) return;

        if (!entityPreviewData?.entityForms?.[suggestion.node_id]) {
          const response = await entityActions?.getFormById(entity._id);
          if (response?.status === 200 && response?.data?.items) {
            const form = response.data.items;
            setEntityPreviewData({
              ...entityPreviewData,
              entityForms: {
                ...entityPreviewData?.entityForms,
                [suggestion.node_id]: form,
              },
            });
            setFormField(form?.form_fields ?? []);
          }
        } else {
          setFormField(entityPreviewData?.entityForms?.[suggestion.node_id]?.form_fields ?? []);
        }

        setContextData({ nodeId: suggestion.node_id, nodeLabel: suggestion.label, fieldType: "entity" });
      } catch (error) {
        console.error(error);
      }
    } else if (suggestion?.node_id && lead_data) {
      const proposalId = lead_data?.campaign?.proposal_id;
      const webFormKey = lead_data?.webForm?.leads_form_id;
      if (proposalId && webFormKey) {
        let formFieldData = webFormList?.[proposalId]?.[webFormKey];
        if (!formFieldData) {
          const response = await leadActions.getFormsByCampaignId(proposalId);
          if (response?.status === 200) {
            const leadForms = response.data ?? {};
            formFieldData = leadForms?.[webFormKey];
            setWebFormList((prev) => ({
              ...prev,
              [proposalId]: leadForms,
            }));
          }
        }
        if (formFieldData) {
          const updateData = modifyWebFormListObj(formFieldData?.leads_form_items || [])?.map(
            (item: any) => ({
              key_name: item?.key_name,
              // alias_name: item?.alias_name,
              label: item?.alias_name,
              name: item?.key_name,
            })
          );
          setFormField(updateData);
          setContextData({ nodeId: suggestion.node_id, nodeLabel: suggestion.label, fieldType: "leadData" });
        }
      }
    } else {
      // keep existing global/webform flow
      if (cursorPosition !== null) {
        const textBeforeCursor = bodyText.slice(0, cursorPosition);
        const textAfterCursor = bodyText.slice(cursorPosition);
        const updatedText =
          textBeforeCursor.replace(/{{@\w*$/, `{{${suggestion.label}}}`) + textAfterCursor;

        setBodyText(updatedText);
        setShowSuggestions(false);

        const updatedParams = {
          global: data.params?.global || [],
          result: data.params?.result || [],
          webform: data.params?.webform || [],
          entity: data.params?.entity || [],
          lead_data: data.params?.lead_data || [],
          global_lead_data: data?.params?.global_lead_data || [],
        };

        if (suggestion.paramType === 'global') {
          updatedParams.global = [...updatedParams.global, suggestion.value];
        } else if (suggestion.paramType === 'result') {
          updatedParams.result = [...updatedParams.result, suggestion.value];
        } else if (suggestion.paramType === 'webform') {
          updatedParams.webform = [...updatedParams.webform, suggestion.value];
        }
        data.onChange(data.id, { body: updatedText, params: updatedParams });
      }
    }
  };

  const handleSelectField = (field: { label: string; name?: string }) => {
    try {
      if (!contextData?.nodeId || cursorPosition === null) return;

      const textBeforeCursor = bodyText.slice(0, cursorPosition);
      const textAfterCursor = bodyText.slice(cursorPosition);

      // const fieldToken = `{{${contextData.nodeLabel}:${field.name}}}`;
      // const updatedText = textBeforeCursor.replace(/{{@\w*$/, fieldToken) + textAfterCursor;
      let fieldToken = '';
      let updatedParams = { ...data.params };
      console.log(contextData, "contextData====>contextData")
      if (contextData.fieldType === "entity") {
        fieldToken = `{{${contextData.nodeLabel}:${field.name}}}`;
        updatedParams.entity = [
          ...(Array.isArray(data?.params?.entity) ? data.params.entity : []),
          `${contextData.nodeLabel}:${field.name}`
        ];
      } else if (contextData.fieldType === "leadData") {
        fieldToken = `{{${contextData.nodeLabel}:${field.name}}}`;
        updatedParams.lead_data = [
          ...(Array.isArray(data?.params?.lead_data) ? data.params.lead_data : []),
          `${contextData.nodeLabel}:${field.name}`
        ];
      } else if (contextData.fieldType === "global_lead_data") {
        fieldToken = `{{${contextData.nodeLabel}:${field.name}}}`;
        updatedParams.global_lead_data = [
          ...(Array.isArray(data?.params?.global_lead_data) ? data.params.global_lead_data : []),
          `${contextData.nodeLabel}:${field.name}`
        ];
      } else {
        // fallback for other types
        fieldToken = `{{${contextData.nodeLabel}:${field.name}}}`;
      }
      console.log(updatedParams, "updatedParams====>updatedParams")

      const updatedText = textBeforeCursor.replace(/{{@\w*$/, fieldToken) + textAfterCursor;

      setBodyText(updatedText);
      setShowSuggestions(false);

      // const updatedEntityParams = [
      //   ...(Array.isArray(data?.params?.entity) ? data.params.entity : []),
      //   `${contextData.nodeLabel}:${field.name}`
      // ];

      data.onChange(data.id, {
        body: updatedText,
        params: { ...data.params, entity: updatedParams.entity ?? [], lead_data: updatedParams?.lead_data ?? [], global_lead_data: updatedParams?.global_lead_data ?? [] },
      });
    } catch (error) {
      console.warn(error)
    }
  };


  return (
    <>
      {/* textarea (unchanged) */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          label={
            <Typography>
              Body <span style={{ color: 'red' }}>*</span>
            </Typography>
          }
          multiline
          minRows={2}
          fullWidth
          ref={textareaRef}
          value={bodyText}
          onChange={handleBodyChange}
          error={bodyText?.length > 4096}
          inputProps={{ maxLength: 4096, style: { paddingBottom: '2rem' } }}
          placeholder="Enter text"
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 14,
            color: bodyText.length > 4095 ? 'error.main' : 'text.secondary',
          }}
        >
          {`${bodyText.length}/${4096}`}
        </Typography>
      </Box>

      {/* suggestions */}
      {showSuggestions && (
        <Box
          sx={{
            position: 'absolute',
            zIndex: 2000,
            minWidth: 300,
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: 2,
            boxShadow: 4,
            p: 1,
          }}
        >
          <List sx={{ width: '100%', maxHeight: 320, overflowY: 'auto' }}>
            {!contextData?.nodeId ? (
              <>
                <ListSubheader sx={{ fontSize: '12px', textTransform: 'uppercase' }}>
                  Parameters
                </ListSubheader>
                {parentResults?.map((s: any, i: number) => (
                  <div key={i}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => hanleSelectParentResult(s)} sx={{ p: '4px 8px' }}>
                        <ListItemText
                          primary={
                            <>
                              <strong>{s?.label}</strong>
                              <span style={{ color: '#6b7280' }}> ({s?.paramType || 'entity'})</span>
                            </>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </div>
                ))}
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.5 }}>
                  <Typography variant="subtitle2">Select Field ({contextData.nodeLabel})</Typography>
                  <Button size="small" onClick={() => setContextData({ nodeId: null, nodeLabel: null, fieldType: null })}>
                    Back
                  </Button>
                </Box>
                {formField?.map((f, idx) => (
                  <div key={idx}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleSelectField(f)} sx={{ p: '4px 8px' }}>
                        <ListItemText
                          primary={
                            <>
                              <strong>{f?.label}</strong>
                              <span style={{ color: '#6b7280' }}> ({f?.name})</span>
                            </>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </div>
                ))}
              </>
            )}
          </List>
        </Box>
      )}
    </>
  );
}

export default RenderTextArea;
